// Builds a link that carries the whole weekend's picks, for anybody, account or
// not. The token lives in the fragment, so nothing about the plan reaches a
// server.

import { useMemo, useState } from 'react';
import { useApp } from '../../data/store.tsx';
import { encodePlan, planLinkUrl } from '../../engine/planlink.ts';

export function PlanLink() {
  const { state, derived } = useApp();
  const [copied, setCopied] = useState('');

  const picked = useMemo(
    () => state.picks.filter((p) => p.userId === state.userId).map((p) => p.setId),
    [state.picks, state.userId],
  );

  const url = useMemo(() => {
    if (picked.length === 0) return '';
    const token = encodePlan(picked, {
      days: state.days.map((d) => d.id),
      stages: state.stages.map((s) => s.id),
      sets: state.sets,
    });
    const origin = typeof window === 'undefined' ? '' : window.location.origin;
    return planLinkUrl(origin, token);
  }, [picked, state.days, state.stages, state.sets]);

  const dayCount = new Set(
    picked.map((id) => derived.setById.get(id)?.dayId).filter((id): id is string => Boolean(id)),
  ).size;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied('Copied. Paste it wherever the crew talks.');
    } catch {
      setCopied('Copying was refused. Select the link below and copy it.');
    }
  };

  return (
    <div className="panel">
      <h3>Send this plan</h3>
      {picked.length === 0 ? (
        <p className="muted" style={{ margin: 0, fontSize: 16 }}>
          Tap sets on the grid and a link appears here. It works without an account.
        </p>
      ) : (
        <>
          <p className="muted" style={{ margin: '0 0 8px', fontSize: 16 }}>
            {picked.length} {picked.length === 1 ? 'set' : 'sets'} across {dayCount}{' '}
            {dayCount === 1 ? 'day' : 'days'}. Anybody who opens this sees the plan and can take it
            as their own. No account, and nothing about it is sent to the server.
          </p>
          <div className="flex" style={{ gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="small" onClick={() => void copy()}>
              Copy the link
            </button>
          </div>
          <input
            className="field"
            style={{ marginTop: 8, width: '100%', fontSize: 14 }}
            readOnly
            value={url}
            onFocus={(event) => event.currentTarget.select()}
            aria-label="Link to this plan"
          />
          {copied !== '' && (
            <p className="muted" style={{ margin: '6px 0 0', fontSize: 16 }} role="status">
              {copied}
            </p>
          )}
        </>
      )}
    </div>
  );
}
